'use strict';

import { format } from 'date-fns';
import { t } from 'i18next';
import { Color, Matrix4, Object3D, Quaternion, Vector3 } from 'three';

import {
  ContextMenuItem,
  SkyViewContextMenuProps,
} from '../../react/components/skyview/context-menu';
import { ReferencePointTransformMode } from '../../react/components/skyview/right-menu/tools/reference-point-transform/reference-point-transform';
import { SkyviewAssetPositionProps } from '../../react/components/skyview/tooltip/skyview-asset-position-tooltip';
import { defaultTheme } from '../../react/themes/default/styled-component/theme';
import { getFileBaseName, getFileExtension } from '../../react/utils/path';
import { SkyMapAxiosServiceFactory } from '../../services/axios/skymap-axios-service-factory';
import { MetaDefinition } from '../../services/metadata/meta-types';
import { roundToDecimals } from '../../utils/math/number';
import { cloneMaterials, getMaterials } from '../../utils/three/material';
import { isDefined } from '../../utils/variables';
import { WebsocketManager } from '../../utils/websocket/websocket-manager';
import { AssetManager } from '../asset-manager';
import { AssetVisibilityManager } from '../asset-visibility-manager';
import cameraManager from '../camera-manager';
import { CoordinateManager } from '../coordinate-manager';
import {
  getMatrix4Tuple,
  saveFileVersionTransformation,
} from '../elements/components/dynamic-asset-file-version-transform';
import { Dxf } from '../elements/dxf';
import { Glb } from '../elements/glb';
import { InputHandler } from '../input-handler';
import { publish, SubscriptionTopic } from '../messaging/pubsub';
import { renderOrders } from '../render-orders';
import {
  KeyInputData,
  MouseInputData,
  TouchInputData,
} from '../utilities/input-event/input-event-data-extractor';
import {
  clamp,
  Coordinates,
  coordinateToPoint3d,
  getQuaternionBetweenVectors,
  Point2d,
  toVector2,
  toVector3,
} from '../utilities/math-helpers';
import { Pick, setRayCasterParamsBasedOnCamera } from '../utilities/pick';
import { PickingSphere } from './picking-sphere';
import { Tool } from './tool';
import { buildTooltipProps } from './tooltip-helper';
import { copyCoordinate } from './utils/context-menu/commands';

/**
 * Calculates a transformation matrix based on four input points.
 */
function calculateTransformationMatrix(
  originalPointA: Vector3,
  originalPointB: Vector3,
  targetPointA: Vector3,
  targetPointB: Vector3,
  scale: number,
) {
  const originalVector = originalPointB.clone().sub(originalPointA);
  const targetVector = targetPointB.clone().sub(targetPointA);
  const quaternion = new Quaternion().setFromUnitVectors(
    originalVector.normalize(),
    targetVector.normalize(),
  );

  // Calculate translation to target point A.
  const translationToTarget = targetPointA.clone().sub(originalPointA);

  // Find the pivot point within the asset.
  const assetPivotPoint = originalPointA.clone();

  // Calculate translation to move pivot point to origin.
  const translationToOrigin = assetPivotPoint.clone().negate();

  // Create matrix to translate pivot point to origin.
  const translationMatrixToOrigin = new Matrix4().makeTranslation(
    translationToOrigin.x,
    translationToOrigin.y,
    translationToOrigin.z,
  );

  // Create matrix for rotation around origin.
  const rotationMatrix = new Matrix4().makeRotationFromQuaternion(quaternion);

  // Create matrix to translate pivot point back to original position.
  const translationMatrixBack = new Matrix4().makeTranslation(
    assetPivotPoint.x,
    assetPivotPoint.y,
    assetPivotPoint.z,
  );

  // Create matrix for scaling.
  const scalingMatrix = new Matrix4().makeScale(scale, scale, scale);

  // Create matrix for translating to target.
  const translationMatrixToTarget = new Matrix4().makeTranslation(
    translationToTarget.x,
    translationToTarget.y,
    translationToTarget.z,
  );

  // Combine matrices: translate to origin -> scale -> rotate -> translate back -> final translation.
  return new Matrix4()
    .premultiply(translationMatrixToOrigin)
    .premultiply(scalingMatrix)
    .premultiply(rotationMatrix)
    .premultiply(translationMatrixBack)
    .premultiply(translationMatrixToTarget);
}

// Yellow
const defaultCursorColor = 0xffff00;

export class ReferencePointTransformTool extends Tool {
  private cursor: PickingSphere;
  private cursorPointA: PickingSphere;
  private cursorPointB: PickingSphere;
  private cursorCoordinateA: PickingSphere;
  private cursorCoordinateB: PickingSphere;
  private intersectionPoint?: Vector3;
  private previewContent?: Object3D;
  _mode: ReferencePointTransformMode = 'coordinates';
  asset?: Glb | Dxf;
  preview?: Object3D;
  _targetScale: number = 1;
  _autoUpdatePreview: boolean = false;

  get mode() {
    return this._mode;
  }

  set mode(value: ReferencePointTransformMode) {
    this.cursorCoordinateA.visible = value === 'coordinates';
    this.cursorCoordinateB.visible = value === 'coordinates';
    this._mode = value;
  }

  get autoUpdatePreview() {
    return this._autoUpdatePreview;
  }
  set autoUpdatePreview(state: boolean) {
    this._autoUpdatePreview = state;

    if (state) {
      this.calculatePreviewTransformation();
    }
  }

  get targetScale() {
    return this._targetScale;
  }

  set targetScale(state: number) {
    this._targetScale = state;
    this.calculatePreviewTransformation();
  }

  private _knownCoordinateA: Coordinates | undefined;
  get knownCoordinateA(): Coordinates | undefined {
    return this._knownCoordinateA;
  }

  set knownCoordinateA(value: Coordinates | undefined) {
    this._knownCoordinateA = value;

    if (!isDefined(value)) {
      return;
    }

    const localPosition = CoordinateManager.getInstance().projectCoordsToLocal(value);
    this.cursorCoordinateA.position.copy(localPosition);

    if (this.autoUpdatePreview) {
      this.calculatePreviewTransformation();
    }
  }

  private _knownCoordinateB: Coordinates | undefined;
  get knownCoordinateB(): Coordinates | undefined {
    return this._knownCoordinateB;
  }

  set knownCoordinateB(value: Coordinates | undefined) {
    this._knownCoordinateB = value;

    if (!isDefined(value)) {
      return;
    }

    const localPosition = CoordinateManager.getInstance().projectCoordsToLocal(value);
    this.cursorCoordinateB.position.copy(localPosition);

    if (this.autoUpdatePreview) {
      this.calculatePreviewTransformation();
    }
  }

  referenceLength: number | undefined;

  calculatePreviewTransformation() {
    if (!isDefined(this.preview) || !isDefined(this.asset)) {
      return;
    }

    const originalPointA = this.cursorPointA.position.clone();
    const targetPointA = this.cursorCoordinateA.position.clone();

    const originalPointB = this.cursorPointB.position.clone().setZ(originalPointA.z);
    const targetPointB = this.cursorCoordinateB.position.clone().setZ(targetPointA.z);

    this.preview.position.set(0, 0, 0);
    this.preview.rotation.set(0, 0, 0);
    this.preview.scale.set(1, 1, 1);

    const referencePointDistance = originalPointA.distanceTo(originalPointB);
    const knownCoordinatesDistance = targetPointA.distanceTo(targetPointB);

    const calculateScale = () => {
      if (
        this.targetScale > 0 &&
        (!this.asset!.canBeScaled() ||
          referencePointDistance === 0 ||
          knownCoordinatesDistance === 0)
      ) {
        return this.targetScale;
      }

      return knownCoordinatesDistance / referencePointDistance;
    };

    const scale = calculateScale();

    const matrix = calculateTransformationMatrix(
      originalPointA,
      originalPointB,
      targetPointA,
      targetPointB,
      scale,
    );

    const origo = CoordinateManager.getInstance().getProjectionOrigo() ?? new Vector3();

    /**
     * This matrix is aligned with the origo. Applying it to a DXF or DWG
     * file will align it geographically with where the preview is.
     */
    const origoAlignedMatrix = calculateTransformationMatrix(
      originalPointA.clone().add(origo),
      originalPointB.clone().add(origo),
      targetPointA.clone().add(origo),
      targetPointB.clone().add(origo),
      scale,
    );

    this.preview.applyMatrix4(matrix);
    this.preview.updateMatrixWorld(true);

    return origoAlignedMatrix;
  }

  scaleFromReferenceLength(referenceLength: number) {
    if (!isDefined(this.asset) || !this.asset.canBeScaled()) {
      throw new Error(`Scaling by reference length is not allowed for this file type.`);
    }

    const referenceVector = new Vector3().subVectors(
      this.cursorPointB.position,
      this.cursorPointA.position,
    );

    const referenceVectorScaled = referenceVector
      .clone()
      .multiplyScalar(referenceLength / referenceVector.length());

    const coordinateA = CoordinateManager.getInstance().localToProjectCoords(
      this.cursorPointA.position,
    );
    const coordinateB = CoordinateManager.getInstance().localToProjectCoords(
      this.cursorPointA.position.clone().add(referenceVectorScaled),
    );

    return this.scaleFromknownCoordinates(coordinateA, coordinateB);
  }

  async createDxfExport() {
    const transform = this.calculatePreviewTransformation();

    if (
      !isDefined(transform) ||
      !isDefined(this.preview) ||
      !isDefined(this.asset) ||
      !isDefined(this.asset.selectedVersion) ||
      !(this.asset instanceof Dxf)
    ) {
      throw new Error('Failed scaling asset. Asset is undefined.');
    }

    const fileResponse = await SkyMapAxiosServiceFactory.instance
      .createFileServiceV1()
      .getFileById({
        path: { fileId: this.asset.selectedVersion.fileId },
      });

    const baseName = getFileBaseName(
      fileResponse.data.name,
      getFileExtension(fileResponse.data.name),
    );
    const exportName = `${baseName}_export_${format(new Date(), 'yyyy-MM-dd_HHmm')}.dxf`;

    await WebsocketManager.instance.waitForWebsocketConnection();
    await SkyMapAxiosServiceFactory.instance.createFileVersionServiceV1().createExport({
      path: { fileVersionId: this.asset.selectedVersion.id },
      body: {
        fileName: exportName,
        folderId: fileResponse.data.folderId ?? undefined,
        options: {
          type: '.dxf',
          transform: transform.toArray(),
          explode: true,
        },
      },
    });

    publish(SubscriptionTopic.WebsocketMessageRecieved, {
      message: 'FileVersionExportUpdated',
      status: 'RUNNING',
      fileName: exportName,
    });
  }

  async scaleFromknownCoordinates(knownCoordinateA: Coordinates, knownCoordinateB: Coordinates) {
    if (!isDefined(this.preview) || !isDefined(this.asset) || !(this.asset instanceof Glb)) {
      throw new Error('Failed scaling asset. Asset is undefined.');
    }

    // Decrease the preview opacity while we process the transformation.
    this.setPreviewOpacity(0.5);

    knownCoordinateA.height = roundToDecimals(knownCoordinateA.height, 4);
    // Make sure point b always has the same height as point a to avoid any tilting of the scaled asset.
    knownCoordinateB.height = knownCoordinateA.height;

    const referenceVector = new Vector3()
      .subVectors(this.cursorPointB.position, this.cursorPointA.position)
      .setZ(0);

    const convertToLocal = (point: Coordinates): Vector3 =>
      toVector3(
        CoordinateManager.getInstance().projectToLocal(point.easting, point.northing),
        point.height - CoordinateManager.getInstance().getMAS(),
      );

    const desiredLocalA = convertToLocal(knownCoordinateA);
    const desiredLocalB = convertToLocal(knownCoordinateB);
    const desired = new Vector3().subVectors(desiredLocalB, desiredLocalA);

    const q = getQuaternionBetweenVectors(referenceVector, desired);
    const scale = this.asset.canBeScaled() ? desired.length() / referenceVector.length() : 1;

    const transformedA = this.cursorPointA.position
      .clone()
      .sub(this.asset.position)
      .applyQuaternion(q)
      .multiplyScalar(scale);

    const newPosition = new Vector3().subVectors(desiredLocalA, transformedA);

    this.asset.visible = false;

    const coordinates = CoordinateManager.getInstance().localToProjectCoords(newPosition);
    coordinates.height = newPosition.z + CoordinateManager.getInstance().getMAS();

    // Transform the texture using 0,0,0 as position.
    const transform = getMatrix4Tuple(this.asset, q, new Vector3(scale, scale, scale), coordinates);
    await saveFileVersionTransformation(this.asset, transform);

    // Update the wgs84 coordinates of the asset manually.
    // This ensures that the transformation will stay the same with various coordinate systems.
    const coords = this.asset.calculateWGS84Coords(toVector2(newPosition));
    this.asset.wgs84 = { lng: coords?.x ?? 0, lat: coords?.y ?? 0 };

    if (this.asset.canBeScaled() && knownCoordinateA.height !== 0) {
      this.asset.MAS = knownCoordinateA.height;
    }

    // Recalculate the position before saving to update the wgs84 coordinate information.
    this.asset.calculateLocalPosition();
    await this.asset.save();

    cameraManager().getActiveController()?.focusOnAsset(this.asset);
  }

  onReferencePointAChanged?: (newValue: Coordinates) => void;
  onReferencePointBChanged?: (newValue: Coordinates) => void;

  tooltipCallback?: (tooltip?: SkyviewAssetPositionProps) => void = undefined;
  contextMenuCallback?: (contextMenu?: SkyViewContextMenuProps) => void = undefined;

  onTooltipChanged(callback: (tooltip?: SkyviewAssetPositionProps) => void) {
    this.tooltipCallback = callback;
  }

  onContextMenuChanged(callback: (contextMenu?: SkyViewContextMenuProps) => void) {
    this.contextMenuCallback = callback;
  }

  constructor(inputHandler: InputHandler) {
    super('reference-point-transform', inputHandler);
    this.visible = false;

    const blue = new Color(defaultTheme.color.blue);
    const green = new Color(defaultTheme.color.green);

    this.cursor = new PickingSphere(new Color(defaultCursorColor));

    this.cursorPointA = new PickingSphere(blue, 10, 'round');
    this.cursorPointB = new PickingSphere(green, 10, 'round');
    this.cursorCoordinateA = new PickingSphere(blue, 15, 'cross');
    this.cursorCoordinateB = new PickingSphere(green, 15, 'cross');

    this.add(this.cursor);
    this.add(this.cursorCoordinateA);
    this.add(this.cursorCoordinateB);

    this.reset();
  }

  reset() {
    if (this.preview) {
      AssetManager.instance.removeTemp(this.preview);
      this.preview = undefined;
      this.previewContent = undefined;
    }

    this.mode = 'coordinates';
    this.cursorCoordinateA.visible = false;
    this.cursorCoordinateB.visible = false;
    this.targetScale = 1;
    this.autoUpdatePreview = false;
    this.asset = undefined;
  }

  getMeta(sweref: string): MetaDefinition {
    return {
      src: 'tool.reference-point-transform',
      parameters: undefined,
    };
  }

  onTouchStart(e: TouchInputData) {
    return this.onTouchMove(e);
  }

  onTouchMove(e: TouchInputData) {
    return true;
  }

  onMouseOut(e: MouseInputData) {
    this.tooltipCallback?.();
    return true;
  }

  onProjectProjectionUpdated() {
    const origoDifference = CoordinateManager.getInstance().origoDifference;

    this.cursor?.position.add(origoDifference);
    this.cursorCoordinateA?.position.add(origoDifference);
    this.cursorCoordinateB?.position.add(origoDifference);
    this.cursorPointA?.position.add(origoDifference);
    this.cursorPointB?.position.add(origoDifference);
    this.previewContent?.position.add(origoDifference);

    this.calculatePreviewTransformation();

    return true;
  }

  refreshPointerAndUi(e: MouseInputData) {
    if (this.mouseMoved(e)) {
      this.contextMenuCallback?.();
    }

    const camera = cameraManager().getActiveCamera();

    if (!camera) {
      return true;
    }

    const pixelSnapThreshold = 20;
    const minPickingDistance = 0.1;
    const maxPickingDistance = 350;

    const rayCaster = camera.getRay(e.pointer.position.x, e.pointer.position.y);
    setRayCasterParamsBasedOnCamera(
      camera.getTHREECamera(),
      rayCaster,
      pixelSnapThreshold,
      clamp(
        this.cursor.position.distanceTo(rayCaster.ray.origin),
        minPickingDistance,
        maxPickingDistance,
      ),
    );

    this.intersectionPoint = this.preview?.visible
      ? Pick.instance.objectPoint(rayCaster, [this.preview])
      : undefined;

    const cursorPoint =
      this.intersectionPoint ??
      Pick.instance.closestPoint(camera, rayCaster) ??
      Pick.instance.height(e.pointer.position.x, e.pointer.position.y, this.asset?.position.z ?? 0);

    if (cursorPoint) {
      this.cursor.position.set(cursorPoint.x, cursorPoint.y, cursorPoint.z);
    }

    this.cursor.visible = isDefined(cursorPoint);
    this.updateTooltip(e.pointer.client);
  }

  onKeyDown(input: KeyInputData) {
    if (this.cursor.visible) {
      this.inputHandler.handleKeyPress(
        input,
        'c',
        () => {
          const coordinates = CoordinateManager.getInstance().localToProjectCoords(
            this.cursor.position,
          );
          void copyCoordinate(coordinateToPoint3d(coordinates)).action();
        },
        'Control',
      );
    }
  }

  onMouseWheel(e: MouseInputData) {
    this.refreshPointerAndUi(e);
    return true;
  }

  onMouseMove(e: MouseInputData) {
    this.refreshPointerAndUi(e);
    return true;
  }

  onMouseDown(e: MouseInputData) {
    this.mousePosition = e.pointer.client;
    return true;
  }

  async onMouseUp(e: MouseInputData) {
    if (!this.mousePosition || e.pointer.client.distanceTo(this.mousePosition) > 10) {
      return true;
    }

    this.updateContextMenu(e, this.intersectionPoint);

    return Promise.resolve(true);
  }

  setPreviewOpacity(value: number) {
    if (!isDefined(this.preview)) {
      return;
    }
    const opacity = clamp(value, 0, 1);
    const renderOrder = renderOrders.referencePointTransformToolPreview;
    this.preview.visible = opacity > 0;

    this.preview.renderOrder = renderOrder;
    this.preview.traverse((node) => {
      node.renderOrder = renderOrder;
      const materials = getMaterials(node);
      for (const material of materials) {
        material.opacity = opacity;
        material.transparent = true;
        material.depthWrite = false;
        material.depthTest = true;
        material.needsUpdate = true;
      }
    });
  }

  onInit(asset: Glb | Dxf) {
    if (asset.uuid !== this.asset?.uuid) {
      this.reset();
    }

    if (asset.loadingStatus !== 'loaded') {
      throw new Error('Failed to initialize reference point transform tool. Asset is not loaded.');
    }

    this.asset = asset;
    this.preview = new Object3D();

    this.previewContent = new Object3D();
    this.previewContent.position.copy(asset.position);
    this.previewContent.rotation.copy(asset.rotation);
    this.previewContent.scale.copy(asset.scale);
    this.previewContent.updateMatrixWorld(true);
    this.previewContent.add(...asset.children.map((x) => x.clone(true)));

    // Clone the materials so original asset is not affected when changing opacity on the preview.
    this.previewContent.traverse((node) => cloneMaterials(node));
    this.setPreviewOpacity(1);

    this.preview.add(this.previewContent);

    // Ensure the assets matrix world is updated before generating the bounding box.
    asset.updateMatrixWorld(true);

    // Position and add the cursors to the preview.
    const bb = asset.generateBoundingBox();
    const min = asset.localToWorld(bb.min.clone());
    const max = asset.localToWorld(bb.max.clone()).setZ(min.z);
    this.updatePointA(min.clone());
    this.updatePointB(max.clone());

    publish(
      SubscriptionTopic.OnReferencePointTransformCoordinateASet,
      CoordinateManager.getInstance().localToProjectCoords(min.clone()),
    );

    publish(
      SubscriptionTopic.OnReferencePointTransformCoordinateBSet,
      CoordinateManager.getInstance().localToProjectCoords(max.clone()),
    );

    this.preview.add(this.cursorPointA);
    this.preview.add(this.cursorPointB);
    this.cursorCoordinateA.visible = true;
    this.cursorCoordinateB.visible = true;

    AssetManager.instance.addTemp(this.preview);
  }

  updateTooltip(screenPoint: Point2d) {
    if (!this.cursor.visible) {
      return;
    }

    const projectionPoint = CoordinateManager.getInstance().localToProjectCoords(
      this.cursor.position,
    );

    const camera = cameraManager().getActiveCamera();
    if (!camera) {
      return;
    }

    this.tooltipCallback?.(
      buildTooltipProps(this.asset, camera, projectionPoint, {
        x: screenPoint.x,
        y: screenPoint.y,
      }),
    );
  }

  updatePointA(newPoint: Vector3) {
    const coordinates = CoordinateManager.getInstance().localToProjectCoords(newPoint);
    this.cursorPointA.position.copy(newPoint);
    this.onReferencePointAChanged?.(coordinates);

    if (this.autoUpdatePreview) {
      this.calculatePreviewTransformation();
    }
  }

  updatePointB(newPoint: Vector3) {
    const coordinates = CoordinateManager.getInstance().localToProjectCoords(newPoint);
    this.cursorPointB.position.copy(newPoint);
    this.onReferencePointBChanged?.(coordinates);

    if (this.autoUpdatePreview) {
      this.calculatePreviewTransformation();
    }
  }

  updateContextMenu(e: MouseInputData, intersectionPoint?: Vector3) {
    const items: ContextMenuItem[] = [];

    if (isDefined(this.preview) && isDefined(this.asset)) {
      items.push(
        {
          title: t('contextMenu.putPointA', { ns: 'skyviewScale' }),
          disabled: !isDefined(intersectionPoint),
          action: () => {
            if (this.preview && this.intersectionPoint) {
              this.updatePointA(this.preview.worldToLocal(this.intersectionPoint.clone()));
            }
            this.contextMenuCallback?.();
          },
        },
        {
          title: t('contextMenu.putPointB', { ns: 'skyviewScale' }),
          disabled: !isDefined(intersectionPoint),
          action: () => {
            if (this.preview && this.intersectionPoint) {
              this.updatePointB(this.preview.worldToLocal(this.intersectionPoint.clone()));
            }
            this.contextMenuCallback?.();
          },
        },
      );

      if (this.mode === 'coordinates') {
        items.push(
          {
            title: t('contextMenu.setKnownCoordinateA', { ns: 'skyviewScale' }),
            action: () => {
              publish(
                SubscriptionTopic.OnReferencePointTransformCoordinateASet,
                CoordinateManager.getInstance().localToProjectCoords(this.cursor.position.clone()),
              );
              this.contextMenuCallback?.();
            },
          },
          {
            title: t('contextMenu.setKnownCoordinateB', { ns: 'skyviewScale' }),
            action: () => {
              publish(
                SubscriptionTopic.OnReferencePointTransformCoordinateBSet,
                CoordinateManager.getInstance().localToProjectCoords(this.cursor.position.clone()),
              );
              this.contextMenuCallback?.();
            },
          },
        );
      }
    }

    items.push({
      title: t('cancel', { ns: 'common' }),
      hotkey: 'Esc',
      action: () => {
        this.inputHandler.setTool('select');
        this.contextMenuCallback?.();
      },
    });

    this.contextMenuCallback?.({
      pos: {
        x: e.viewportPos?.clientX ?? 0,
        y: e.viewportPos?.clientY ?? 0,
      },
      items,
    });
  }

  enable() {
    this.visible = true;
    this.inputHandler.setCursor('crosshair');
  }

  disable() {
    this.visible = false;
    this.inputHandler.setCursor('');

    // Display the original asset when closing the tool since the tool automatically loads
    // and hides the original asset when opened.
    if (isDefined(this.asset) && !this.asset.getStateVisibility()) {
      void AssetVisibilityManager.getInstance().toggleAsset(this.asset.uuid, true, false);
    }

    this.reset();
  }
}
